import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../tokens/cn';

export const TooltipProvider = TooltipPrimitive.Provider;
export const TooltipRoot = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 px-2.5 py-1.5 text-xs font-medium rounded-tk-sm shadow-tk-popover',
        'bg-[var(--tk-surface-fg)] text-[var(--tk-surface-bg-elevated)]',
        'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out',
        'data-[state=delayed-open]:fade-in-0 data-[state=closed]:fade-out-0',
        'data-[state=delayed-open]:zoom-in-95 data-[state=closed]:zoom-out-95',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = 'TooltipContent';

export interface TooltipProps
  extends Omit<ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>, 'children'> {
  content: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Extra classes for the content panel — useful for max-width / wrapping. */
  contentClassName?: string;
  children: ReactNode;
}

/**
 * Convenience wrapper for the common case: trigger + content. For full control
 * use TooltipRoot + TooltipTrigger + TooltipContent directly.
 */
export const Tooltip = ({
  content,
  side = 'top',
  align = 'center',
  delayDuration = 200,
  contentClassName,
  children,
  ...rootProps
}: TooltipProps) => (
  <TooltipRoot delayDuration={delayDuration} {...rootProps}>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side={side} align={align} className={contentClassName}>
      {content}
    </TooltipContent>
  </TooltipRoot>
);
